import { DSLExecuteError } from '@commenda/api/commenda-os-dsl/errors';
import type { ICommendaOSWriteFailure } from '@commenda/api/form-builder/form-submissions';
import { OwnershipErrorCode } from '@commenda/api/ownership/errors';
import type { FormComponent } from '@commenda/types';

import { ComponentType } from '../types';

type SubmitResult = {
  entity: string;
  success: boolean;
  fieldId?: string;
  error?: string;
  code?: string;
};

/**
 * A submit handler that failed part-way can attach the value the form should still save.
 *
 * Writes it already made are in the database. A saved value that does not record them makes the
 * retry repeat them rather than update them, which is how a second entity owner gets minted for
 * one owner and the ownership pool is then exceeded.
 */
export interface ISubmitHandlerRejectionValue {
  readonly valueToPersist: Record<string, unknown>;
}

export class CommendaOSFailureHelper {
  /** Kept under the backend's own reporting cap so a long list never grows the request that carries the answers. */
  static readonly MAX_REPORTED_FAILURES = 25;

  static readonly MAX_ERROR_CHARS = 300;

  /**
   * Backend codes whose message is written for developers; the customer reads this instead.
   * Worded for the form, which has no significant-control checkbox to point at: here a 0-share
   * person only comes from a percentage that rounds to no whole share or unit.
   */
  static readonly CUSTOMER_MESSAGE_BY_ERROR_CODE: ReadonlyMap<string, string> =
    new Map([
      [
        OwnershipErrorCode.ADD_ZERO_PERCENT_REQUIRES_HAS_SIGNIFICANT_CONTROL,
        'An owner would get 0 shares or units. Add more shares or units, or change the percentages.',
      ],
    ]);

  /** Reads back as one sentence a customer can act on, so the retry is not a guess. */
  static describeHandlerRejections(
    handlerRejections: readonly { reason: unknown }[]
  ): string {
    return Array.from(
      new Set(
        handlerRejections.map(({ reason }) => {
          const code = CommendaOSFailureHelper.getErrorCode(reason);
          return (
            (code &&
              CommendaOSFailureHelper.CUSTOMER_MESSAGE_BY_ERROR_CODE.get(code)) ||
            CommendaOSFailureHelper.reasonText(reason)
          );
        })
      )
    ).join(' ');
  }

  /**
   * Title and message for a submit a handler rejected. Only an ownership field is sent back to
   * the ownership section; other handlers (indirect tax registration) register the same way.
   */
  static getHandlerRejectionNotification(
    handlerRejections: readonly { handlerFieldId: string; reason: unknown }[],
    componentMap: Readonly<Record<string, FormComponent>>
  ): { title: string; message: string } {
    const detail =
      CommendaOSFailureHelper.describeHandlerRejections(handlerRejections);
    const isOwnershipOnly = handlerRejections.every(
      ({ handlerFieldId }) =>
        componentMap[handlerFieldId]?.component === ComponentType.Ownership
    );
    return isOwnershipOnly
      ? {
          title: 'Ownership details were not saved',
          message: `Your answers are saved. ${detail} Please review the ownership section and submit again.`,
        }
      : {
          title: 'Some details were not saved',
          message: `Your answers are saved. ${detail} Please review and submit again.`,
        };
  }

  /** The same, for a rejected field write. */
  static describeCommendaOSFailures(
    failures: readonly ICommendaOSWriteFailure[]
  ): string {
    const messages = Array.from(
      new Set(
        failures
          .map(
            (failure) =>
              (failure.code &&
                CommendaOSFailureHelper.CUSTOMER_MESSAGE_BY_ERROR_CODE.get(
                  failure.code
                )) ||
              failure.error
          )
          .filter((message): message is string => Boolean(message))
      )
    );
    if (messages.length > 0) return messages.join(' ');
    const entities = Array.from(
      new Set(failures.map((failure) => failure.entity))
    );
    return `Could not save ${entities.join(', ') || 'these details'}.`;
  }

  /**
   * A systemic failure produces one entry per attempted write, so the list is deduped and
   * capped before it travels; the final entry states how many were dropped.
   */
  static collectCommendaOSFailures(
    results: readonly SubmitResult[],
    handlerRejections: readonly { handlerFieldId: string; reason: unknown }[],
    fallbackError?: string
  ): ICommendaOSWriteFailure[] {
    const collected: ICommendaOSWriteFailure[] = results
      .filter((result) => !result.success)
      .map(CommendaOSFailureHelper.toFailure);

    handlerRejections.forEach(({ handlerFieldId, reason }) => {
      collected.push({
        entity: 'submitHandler',
        fieldId: handlerFieldId,
        error: CommendaOSFailureHelper.reasonText(reason).slice(
          0,
          CommendaOSFailureHelper.MAX_ERROR_CHARS
        ),
      });
    });

    if (collected.length === 0 && fallbackError) {
      collected.push({
        entity: 'commendaOS',
        error: fallbackError.slice(0, CommendaOSFailureHelper.MAX_ERROR_CHARS),
      });
    }

    const deduped = Array.from(
      new Map(
        collected.map((failure) => [
          `${failure.entity}|${failure.fieldId ?? ''}|${failure.error ?? ''}`,
          failure,
        ])
      ).values()
    );

    if (deduped.length <= CommendaOSFailureHelper.MAX_REPORTED_FAILURES) {
      return deduped;
    }

    const kept = deduped.slice(
      0,
      CommendaOSFailureHelper.MAX_REPORTED_FAILURES - 1
    );
    return [
      ...kept,
      {
        entity: 'truncated',
        error: `${deduped.length - kept.length} further failures omitted`,
      },
    ];
  }

  // A handler rejects with whatever it threw; OwnershipWriteError keeps the DSL error as its cause.
  static getErrorCode(reason: unknown): string | undefined {
    if (reason instanceof DSLExecuteError) return reason.code;
    if (reason instanceof Error && reason.cause instanceof DSLExecuteError) {
      return reason.cause.code;
    }
    return undefined;
  }

  static partialValueFromRejection(
    reason: unknown
  ): Record<string, unknown> | null {
    if (reason == null || typeof reason !== 'object') return null;
    const carried = (reason as Partial<ISubmitHandlerRejectionValue>)
      .valueToPersist;
    return carried != null && typeof carried === 'object' ? carried : null;
  }

  private static toFailure(result: SubmitResult): ICommendaOSWriteFailure {
    return {
      entity: result.entity,
      ...(result.fieldId ? { fieldId: result.fieldId } : {}),
      ...(result.code ? { code: result.code } : {}),
      ...(result.error
        ? {
            error: result.error.slice(
              0,
              CommendaOSFailureHelper.MAX_ERROR_CHARS
            ),
          }
        : {}),
    };
  }

  private static reasonText(reason: unknown): string {
    if (reason instanceof Error) return reason.message;
    if (typeof reason === 'string') return reason;
    return JSON.stringify(reason) ?? String(reason);
  }
}
